import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';

import type { StaffNotificationPage } from '@nucleus-commerce/api-client/admin';

import { staffNotificationsApi } from '../api/production-api';

import { productionKeys } from './production-keys';

type Pages = InfiniteData<StaffNotificationPage, string>;

/** Marking one read is an idempotent toggle, so the bell updates before the backend answers. */
export function useReadNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => staffNotificationsApi.read(id),
    onMutate: (id) => {
      // Both lists (all, unread only) hold the alert and the count; each is patched in place.
      queryClient.setQueriesData<Pages>({ queryKey: productionKeys.notifications() }, (data) => {
        if (!data) return data;
        const target = data.pages.flatMap((p) => p.items).find((n) => n.id === id);
        if (!target || target.readAt) return data;
        const readAt = new Date().toISOString();
        return {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            unreadCount: Math.max(0, page.unreadCount - 1),
            items: page.items.map((n) => (n.id === id ? { ...n, readAt } : n)),
          })),
        };
      });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: productionKeys.notifications() }),
  });
}

export function useReadAllNotifications() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => staffNotificationsApi.readAll(),
    meta: { successMessage: 'production:toasts.allRead' },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: productionKeys.notifications() }),
  });
}
